import React, { createContext, ReactNode, useCallback, useEffect, useState } from 'react';
import LoaderTransition from './LoaderTransition';

export interface LoadingContextType {
     loadingStates: { [key: string]: boolean };
     setLoadingState: (key: string, loading: boolean) => void;
     clearAllLoadings: () => void; // Nova função para limpar todos os loadings
     isAnyLoading: boolean;
     isGlobalLoading: boolean;
     withLoading: (key: string, operation: () => Promise<any>) => Promise<any>;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
     const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});

     const setLoadingState = useCallback((key: string, loading: boolean) => {
          // console.log(`🔄 [LoadingContext] setLoadingState(${key}, ${loading})`); // Reduzir logs
          setLoadingStates((prev) => {
               // PROTEÇÃO: Se o estado já é o mesmo, não atualiza para evitar re-renders desnecessários
               if (prev[key] === loading) {
                    // console.log(`⏸️ [LoadingContext] Estado ${key} já é ${loading}, ignorando`); // Reduzir logs
                    return prev;
               }

               return {
                    ...prev,
                    [key]: loading,
               };
          });
     }, []);

     const withLoading = useCallback(
          async (key: string, operation: () => Promise<any>) => {
               setLoadingState(key, true);
               try {
                    return await operation();
               } finally {
                    setLoadingState(key, false);
               }
          },
          [setLoadingState],
     );

     // Função para limpar todos os loadings forçadamente
     const clearAllLoadings = useCallback(() => {
          console.log('🧹 [LoadingContext] Limpando TODOS os loadings');
          setLoadingStates({});
     }, []);

     const isAnyLoading = Object.values(loadingStates).some(Boolean);

     // Lista de chaves que devem ativar o loader global (overlay)
     const globalLoadingKeys = ['page-loading', 'auth-loading', 'app-loading', 'route-transition'];

     // Verifica se algum loading global está ativo
     const isGlobalLoading = globalLoadingKeys.some((key) => loadingStates[key]);

     // Expõe função de emergência no console para debug
     useEffect(() => {
          (window as any).clearAllLoadings = clearAllLoadings;
          (window as any).debugLoadings = () => {
               console.log('🔍 [Debug] Estados de loading atuais:', loadingStates);
               console.log('🔍 [Debug] isGlobalLoading:', isGlobalLoading);
          };

          return () => {
               delete (window as any).clearAllLoadings;
               delete (window as any).debugLoadings;
          };
     }, [clearAllLoadings, loadingStates, isGlobalLoading]);

     // Listener para detectar mudanças de visibilidade da página
     useEffect(() => {
          const handleVisibilityChange = () => {
               if (document.visibilityState === 'visible') {
                    console.log('🔍 [LoadingContext] Página ficou visível - verificando loadings órfãos');

                    // Se há loadings ativos mas não deveria ter (usuário já autenticado)
                    const hasOrphanLoadings = isGlobalLoading;
                    const isAuthenticated = localStorage.getItem('supabase.auth.token') || document.cookie.includes('sb-') || window.location.pathname === '/';

                    if (hasOrphanLoadings && isAuthenticated) {
                         console.log('🧹 [LoadingContext] Limpando loadings órfãos após maximizar');
                         setTimeout(() => {
                              globalLoadingKeys.forEach((key) => {
                                   setLoadingState(key, false);
                              });
                         }, 500); // Delay para evitar conflitos
                    }
               }
          };

          document.addEventListener('visibilitychange', handleVisibilityChange);

          return () => {
               document.removeEventListener('visibilitychange', handleVisibilityChange);
          };
     }, [isGlobalLoading, setLoadingState]);

     return (
          <LoadingContext.Provider value={{ loadingStates, setLoadingState, clearAllLoadings, isAnyLoading, isGlobalLoading, withLoading }}>
               {children}
               <LoaderTransition isLoading={isGlobalLoading} />
          </LoadingContext.Provider>
     );
};

export default LoadingContext;
